// The voice controller (BUILD 2 C2-8, WP2-8 steps 1, 3, 7, 8, 9 and 12): the
// state machine in ./store.ts, driving the microphone, the voice session, the
// auto-send guards, the undo window, the send and read-aloud.
//
// - Entering (a tap) claims the microphone as 'voice', resumes the shared
//   AudioContext and starts the capture and the mint inside the gesture. The
//   microphone stays claimed, with the indicator showing, until voice mode
//   pauses or ends; frames reach the session only while a turn is open.
// - A committed utterance goes through the guards (./guards.ts). No chat
//   request is made inside the undo window; a Send happens only at its end, on
//   Send now, or never (Esc, ×, a draft).
// - The turn is an ordinary chat turn (GR4): the utterance's text becomes the
//   user message through ./turn-body.ts, with the C2-7 `voice` body. Nothing
//   here reads transcript text or routes on it; `voice_control` effects come
//   only from a model tool call (./voice-control-card.tsx).
// - Barge-in (./barge-in.ts): a tap or sustained microphone energy above the
//   playback reference stops a reply locally in every hands-free session.
//   Once stopped, the rest of that reply is shown but
//   never read, and the next turn says where it stopped (`interrupted`).
// - Exits: Exit, Esc with nothing pending, idle (hands-free 30 s), a thread
//   switch or sign-out (VoiceRoot unbinds), the page
//   hidden, a meeting claiming the microphone. A pending turn is then kept as
//   a draft and never sent.
import type { CreateAppendMessage } from '@assistant-ui/react';
import { createLaneToolTiming, parseToolTiming } from './lane-tool-timing';
import { isVoiceActivity, levelFromRms } from '@/speech/capture/level';
import type {
  CaptureHandle,
  CaptureLifecycle,
  CaptureOptions,
  MicOwner,
  PcmFrame,
} from '@/speech/contracts';
import type { CommittedMessage, ServerMessage } from '@/speech/transport/protocol';
import type { SpeechConversation } from '@/speech/conversation';
import { createBargeIn, type BargeIn } from './barge-in';
import { createVoiceCaptureGate } from './capture-gate';
import { createCapturedOnset } from './capture-onset';
import { markInterrupted, clearInterruptions } from './interruption';
import {
  insertUtterance,
  keepDraft,
  restoreDrafts,
  clearDrafts,
  type ComposerLike,
} from './composer-insert';
import type {
  PlaybackStop,
  SpeakSource,
  SpeechPlayer,
  Utterance,
  VoiceCommit,
  VoiceConfig,
  VoiceConfigState,
  VoiceControlResult,
  VoiceFocus,
  VoicePrefs,
  VoiceStyle,
} from './contracts';
import { commitOutcome, sendOutcome, type CommitOutcome, type GuardId } from './guards';
import { voiceIssue, voiceIssueFor, type VoiceIssue } from './issues';
import type { LiveAnswer } from './live-answer';
import { ENDPOINT_MIN_WORDS, VOICE_POLICY } from './policy';
import type { VoiceCommitted, VoiceLinkEvents, VoiceReady, VoiceTimers } from './session';
import {
  barActive,
  replying,
  upsertSegment,
  type CaptionSegment,
  type PendingReason,
  type VoiceState,
  type VoiceStore,
} from './store';
import { armVoiceTurn, disarmVoiceTurn, voiceTurnBody, voiceTurnMessage } from './turn-body';
import { createUtterance, isEmptyUtterance, mergeUtterances } from './utterance';
import { createSpokenChannelBridge, type SpokenPart } from './spoken-channel';
import { clearIssuedQuestions, rememberIssuedQuestion } from './confirm/issued-questions';
import {
  clearLaneTiming,
  laneTimingEnabled,
  markLaneSpeechEnded,
  markLaneSpokenText,
  publishLaneTiming,
  setLaneTimingEnabled,
} from './lane-timing';

/** What voice mode needs from the thread it is mounted in (VoiceRoot binds it). */
export type VoiceBinding = {
  /** The conversation, when the mount knows it; drafts are kept per thread. */
  threadId: string | null;
  /** The signed-in person; a change clears kept drafts. */
  userId: string | null;
  append(message: CreateAppendMessage): void;
  composer: ComposerLike & { attachments(): number };
  running(): boolean;
  /** Stop the run in flight (the existing stop and resume cancellation). */
  cancelRun(): void | Promise<void>;
  latestAssistantMessageId(): string | null;
  speechLanguages?(): string[] | null;
  /** The question gate (sign-in, company, billing); may open its dialog. */
  gateAllows(): boolean;
  focusComposer(): void;
};

/**
 * The thread as VoiceRoot sees it: whether it runs, and its last message with
 * its visible text and how many parts it has (an assistant message with no
 * part yet is the runtime's placeholder, not a reply).
 */
export type ThreadSnapshot = {
  running: boolean;
  last: { id: string; role: string; text: string; parts: number } | null;
};

export type VoiceLinkApi = {
  readonly sessionId: string | null;
  readonly openTurn: number | null;
  open(): void;
  beginTurn(): number;
  push(frame: PcmFrame): void;
  commit(): Promise<VoiceCommitted>;
  close(): void;
};

export type VoiceControllerDeps = {
  store: VoiceStore;
  micOwner: MicOwner;
  startCapture(options: CaptureOptions): Promise<CaptureHandle>;
  createLink(
    style: VoiceStyle,
    events: VoiceLinkEvents,
    conversation?: () => SpeechConversation,
  ): VoiceLinkApi;
  player?: SpeechPlayer;
  focus: VoiceFocus;
  timers: VoiceTimers;
  /** A monotonic clock in ms (`performance.now()`, the clock of `Utterance.startedAt`). */
  now(): number;
  visible(): boolean;
  online(): boolean;
  /** The shared AudioContext (resumed on entry, and its latencies for the echo gate). */
  audioContext(): AudioContext | null;
  config(): VoiceConfigState;
  setActive(active: boolean): void;
  liveAnswer(): LiveAnswer | null;
  /** Best effort; null when the browser has none. */
  wakeLock?(): Promise<{ release(): Promise<void> } | null>;
  /**
   * Full duplex passed Gate B on this platform (C2-3,
   * `PONEVA_CHAT_SPEECH_VOICE_FULL_DUPLEX_PLATFORMS`). Absent or false: half
   * duplex, whatever the person's `fullDuplex` says. The runtime compares
   * the actual platform with the deployment's verified allowlist.
   */
  fullDuplexAvailable?(): boolean;
  /** PUT /api/v1/speech/voice-preferences, then a config refresh. */
  savePrefs(patch: Partial<VoicePrefs>): Promise<void>;
};

export type VoiceController = {
  enter(): void;
  exit(): void;
  /** Tap-to-toggle: the first tap opens the turn, the second commits it. */
  toggleTalk(): void;
  /** Slide off, ✕ or Esc while hearing: the turn is dropped, never sent. */
  cancelTalk(): void;
  /** ✓ in hands-free: commit what was heard now (while a reply plays: stop it). */
  commitNow(): void;
  sendNow(): void;
  editPending(): void;
  discardPending(): void;
  /** Esc: the pending turn, then the open turn, then voice mode. */
  escape(): void;
  /** Stop the reply being read (a tap or key over it); the rest of it is not read. */
  stopSpeaking(): void;
  /** Leave a paused or ended state's notice. */
  dismiss(): void;
  bind(binding: VoiceBinding): () => void;
  onThread(snapshot: ThreadSnapshot): void;
  /** The page became hidden or visible. */
  onVisibility(visible: boolean): void;
  /**
   * A `voice_control` result in message `messageId`: applied once, and only
   * when that message is the reply to the turn this client last sent in its
   * open voice session.
   */
  applyControl(toolCallId: string, result: VoiceControlResult, messageId: string | null): boolean;
  onSpokenOutput(value: unknown, resumed?: boolean): void;
  onToolTiming(value: unknown, resumed?: boolean): void;
};

type TurnStart = { startedAt: number; draftText: string; attachments: number };

type Pending = {
  utterance: Utterance;
  binding: VoiceBinding;
  /** The voice session it was committed in (G8): a rollover keeps it, a resume does not. */
  link: VoiceLinkApi;
  /** The server session that committed it (the turn body's `sessionId`). */
  sessionId: string;
  provider: string;
  degraded: boolean;
  timer: unknown;
  undoMs: number;
  committedAt: number;
};

type Live = {
  binding: VoiceBinding;
  link: VoiceLinkApi;
  sessionId: string;
  sentAt: number;
  /** The last assistant message before the send; the reply is a newer one. */
  before: string | null;
  messageId: string | null;
  /** The thread ran for this turn (it runs from the append, before the request goes out). */
  ran: boolean;
  /** The reply appeared: the request with the body has gone out. */
  started: boolean;
  done: boolean;
  firstAudioAt: number | null;
  spokenTextAt: number | null;
  readAloud: boolean;
  /**
   * The reply's thread: the binding's, else the live answer's while it streams
   * (kept, so the reply's last `feed` still names it once the stream clears).
   */
  threadId: string | null;
  spokenParts: Map<number, SpokenPart>;
  spokenFed: string;
  nextSpokenSequence: number;
  toolTiming: ReturnType<typeof createLaneToolTiming>;
};

export function createVoiceController(deps: VoiceControllerDeps): VoiceController {
  const captureGate = createVoiceCaptureGate(deps.now);
  const { store } = deps;
  const set = (patch: Partial<VoiceState>) => store.setState(patch);
  const get = () => store.getState();

  let binding: VoiceBinding | null = null;
  let lastBoundUserId: string | null = null;
  let generation = 0;
  let link: VoiceLinkApi | null = null;
  let ready: VoiceReady | null = null;
  let capture: Promise<CaptureHandle | null> | null = null;
  let release: (() => void) | null = null;
  let barge: BargeIn | null = null;
  let wake: { release(): Promise<void> } | null = null;
  let turnStart: TurnStart | null = null;
  let lastVoicedAt: number | null = null;
  let pending: Pending | null = null;
  let cancelling: Pending | null = null;
  let pendingId = 0;
  let live: Live | null = null;
  let lastInterrupted: PlaybackStop | null = null;
  let lastFirstAudioMs: number | null = null;
  let lastReplyMessageId: string | null = null;
  /** The reply before the latest turn's: what "repeat" replays by default. */
  let previousReplyMessageId: string | null = null;
  /** The latest voice turn this client sent, kept until the next send or an exit. */
  let lastSent: { binding: VoiceBinding; link: VoiceLinkApi; messageId: string | null } | null =
    null;
  let idleTimer: unknown = null;
  let degradedToUtterance = false;
  const applied = new Set<string>();
  const spoken = createSpokenChannelBridge((part, origin) => {
    const turn = live;
    if (!turn || !turn.readAloud || !turn.messageId || turn.messageId !== origin.messageId) return;
    if (part.confirmation) {
      const { ref, ...summary } = part.confirmation;
      rememberIssuedQuestion(ref, { ...summary, text: part.text, messageId: origin.messageId });
    }
    turn.spokenParts.set(part.sequence, part);
    emitSpoken(turn, false);
  });

  const voiceConfig = (): VoiceConfig | null => deps.config().voice;
  const prefs = () => voiceConfig()?.prefs ?? null;
  const setPhase = (phase: VoiceState['phase'], patch: Partial<VoiceState> = {}) => {
    set({ phase, ...patch });
    deps.setActive(barActive(phase));
  };

  // ── Read-aloud and the policy switches ───────────────────────────────────

  const readAloudOn = () => {
    const config = deps.config();
    const person = config.voice?.prefs;
    return Boolean(
      config.readAloud?.available &&
      config.voice?.policy.readAloud &&
      person?.readAloud &&
      person.speakWith === 'poneva',
    );
  };
  /** Gate B allows skipping the passive echo tail; local interruption is automatic. */
  const fullDuplexOn = (style: VoiceStyle) =>
    style === 'handsfree' && Boolean(prefs()?.fullDuplex) && deps.fullDuplexAvailable?.() === true;

  /**
   * The person spoke over the reply, or stopped it: playback stops where it
   * was, and the rest of that reply is not read (it is still shown).
   */
  function interruptReply(stop: PlaybackStop | null) {
    if (stop) markInterrupted(stop.messageId);
    lastInterrupted = stop ?? lastInterrupted;
    if (live) live.readAloud = false;
  }

  const undoFor = (_commit: VoiceCommit): number | 'always_ask' => {
    const value = prefs()?.undoMs ?? voiceConfig()?.undoMs;
    return value === 'always_ask' ? value : (value?.endpoint ?? 2000);
  };

  // ── Idle exit ────────────────────────────────────────────────────────────

  const clearIdle = () => {
    if (idleTimer !== null) deps.timers.clearTimeout(idleTimer);
    idleTimer = null;
  };
  const idleExitMs = () => {
    const idle = voiceConfig()?.idleExitSec ?? { handsfree: 30 };
    return idle.handsfree * 1000;
  };
  function resetIdle() {
    clearIdle();
    if (!link) return;
    idleTimer = deps.timers.setTimeout(onIdle, idleExitMs());
  }
  function onIdle() {
    idleTimer = null;
    const phase = get().phase;
    // Only a quiet, ready voice mode ends; anything in progress waits.
    if (phase !== 'ready' || pending || get().segments.length > 0 || live) return resetIdle();
    pause(voiceIssue('idle'));
  }

  // ── Capture ──────────────────────────────────────────────────────────────

  function onFrame(gen: number, frame: PcmFrame) {
    if (gen !== generation || !link) return;
    // The echo gate keeps a reply's own sound from opening a hands-free turn.
    captureGate.push(frame, barge?.micOpen() !== false, (part) => link?.push(part));
  }

  function onLevel(gen: number, rms: number) {
    if (gen !== generation) return;
    const open = link?.openTurn !== null && barge?.micOpen() !== false;
    set({ level: levelFromRms(open ? rms : 0, get().level) });
    if (open && isVoiceActivity(rms)) {
      // Rearming alone must retain the preceding reply's measurements.
      if (lastVoicedAt === null) clearLaneTiming();
      lastVoicedAt = deps.now();
    }
  }

  function onLifecycle(gen: number, event: CaptureLifecycle) {
    if (gen !== generation) return;
    if (event === 'hidden') pause(voiceIssue('hidden'));
    else if (event === 'mic-ended') pause(voiceIssue('device_lost'));
  }

  const stopCapture = async (handle: Promise<CaptureHandle | null> | null) =>
    (await handle?.catch(() => null))?.stop().catch(() => null);

  // ── Leaving: pause (Resume offered), editing, or off ─────────────────────

  function teardown(reason: 'user' | 'exit' | 'hidden') {
    generation += 1;
    cancelling = null;
    captureGate.clear();
    clearIdle();
    const kept = pending;
    pending = null;
    if (kept) deps.timers.clearTimeout(kept.timer);
    if (kept) toDraft(kept);
    link?.close();
    link = null;
    ready = null;
    void stopCapture(capture);
    capture = null;
    release?.();
    release = null;
    barge?.dispose();
    barge = null;
    if (deps.player && deps.player.state() !== 'idle') deps.player.stop(reason);
    void wake?.release().catch(() => {});
    wake = null;
    turnStart = null;
    live = null;
    clearLaneTiming();
    lastSent = null;
    lastInterrupted = null;
    lastFirstAudioMs = null;
    lastReplyMessageId = null;
    previousReplyMessageId = null;
    applied.clear();
    degradedToUtterance = false;
    disarmVoiceTurn();
    return Boolean(kept);
  }

  const leftState = (issue: VoiceIssue | null): Partial<VoiceState> => ({
    segments: [],
    pending: null,
    micClaimed: false,
    level: 0,
    reconnecting: false,
    issue,
  });

  function pause(issue: VoiceIssue | null) {
    if (get().phase === 'off') return;
    const drafted = teardown(issue?.code === 'hidden' ? 'hidden' : 'exit');
    setPhase('paused', leftState(issue ?? (drafted ? voiceIssue('draft_kept') : null)));
  }

  function exit() {
    teardown('exit');
    setPhase('off', leftState(null));
  }

  function enterEditing() {
    teardown('exit');
    setPhase('editing', leftState(null));
    binding?.focusComposer();
  }

  /** A turn that will not be sent: into its thread's composer, or kept for that thread. */
  function toDraft(kept: Pending) {
    if (kept.binding === binding) {
      insertUtterance(kept.binding.composer, kept.utterance);
      return;
    }
    if (kept.binding.threadId) keepDraft(kept.binding.threadId, kept.utterance);
  }

  // ── Entering ─────────────────────────────────────────────────────────────

  function enter() {
    const config = voiceConfig();
    if (!binding || barActive(get().phase)) return;
    if (!config?.available || !config.policy.voiceMode)
      return void set({ issue: voiceIssue('voice_mode_unavailable', config?.reason ?? null) });
    const style: VoiceStyle = 'handsfree';
    setLaneTimingEnabled(laneTimingEnabled(config.laneTiming));
    const gen = ++generation;
    const claim = deps.micOwner.claim(VOICE_POLICY.micKind, () => onPreempted(gen));
    if (!claim) return void set({ issue: voiceIssue('mic_held_by_meeting') });
    release = claim;
    // Inside the gesture: the AudioContext resumes, the capture and the mint start.
    void deps
      .audioContext()
      ?.resume?.()
      .catch(() => {});
    setPhase('arming', { ...leftState(null), style, micClaimed: true, degraded: false });
    barge = createBargeIn({
      player: deps.player,
      timers: deps.timers,
      now: deps.now,
      latency: () => deps.audioContext(),
      voiceActivityLevel: VOICE_POLICY.voiceActivityLevel,
      onBargeIn: (stop, onsetAt) => {
        if (gen !== generation) return;
        interruptReply(stop);
        captureGate.release(onsetAt, (part) => link?.push(part));
        if (replying(get().phase)) setPhase('hearing');
      },
    });
    barge.setFullDuplex(fullDuplexOn(style));
    const activity = createCapturedOnset(deps.player, barge, deps.now);
    const linkBinding = binding;
    link = deps.createLink(style, linkEvents(gen), () => ({
      threadId: linkBinding?.threadId ?? null,
      languages: linkBinding?.speechLanguages?.() ?? null,
    }));
    link.open();
    capture = deps
      .startCapture({
        sources: ['mic'],
        purpose: 'voice',
        onFrame: (frame) => onFrame(gen, frame),
        onActivity: (rms, samples, sampleOffset) => {
          if (gen === generation) activity(rms, samples, sampleOffset);
        },
        onLevel: (rms) => onLevel(gen, rms),
        onLifecycle: (event) => onLifecycle(gen, event),
      })
      .catch((error: unknown) => {
        if (gen === generation) pause(voiceIssueFor(error));
        return null;
      });
    void deps
      .wakeLock?.()
      .then((lock) => {
        if (gen === generation) wake = lock;
        else void lock?.release().catch(() => {});
      })
      .catch(() => {});
    resetIdle();
  }

  function onPreempted(gen: number) {
    if (gen !== generation) return;
    // A meeting (or an enrolment) took the microphone: voice mode ends here.
    release = null;
    pause(voiceIssue('mic_held_by_meeting'));
  }

  // ── The session's events ─────────────────────────────────────────────────

  function linkEvents(gen: number): VoiceLinkEvents {
    const current = () => gen === generation;
    return {
      onReady: (info) => {
        if (!current()) return;
        ready = info;
        set({ reconnecting: false });
        if (get().phase === 'arming') setPhase('ready');
        if (get().style === 'handsfree' && !degradedToUtterance) openTurn();
      },
      onPartial: (key, message) => current() && heard(key, message, false),
      onFinal: (key, message) => current() && heard(key, message, true),
      onEndpoint: (message) => {
        if (!current() || degradedToUtterance || get().style !== 'handsfree') return;
        if (deps.player && deps.player.state() !== 'idle') return;
        const endedAt = lastVoicedAt;
        markLaneSpeechEnded(endedAt);
        publishLaneTiming({
          // Microphone activity is an estimate, not the ASR audio clock.
          endOfSpeechMs: endedAt === null ? null : Math.max(0, deps.now() - endedAt),
        });
        // Fewer words keep the turn open (C2-8 G2); ✓ commits it at any length.
        if (message.words >= ENDPOINT_MIN_WORDS) commitTurn('endpoint', message.words);
      },
      onDegraded: (message) => {
        if (!current()) return;
        set({ degraded: true });
        // Utterance mode has no ends of turn: hands-free becomes tap-to-commit.
        if (message.to === 'utterance') degradedToUtterance = true;
      },
      onReconnecting: () => current() && set({ reconnecting: true }),
      onReplaced: () => current() && pause(voiceIssue('replaced')),
      onFailure: (issue, cause) =>
        current() && pause(cause === undefined ? voiceIssue(issue.code) : voiceIssueFor(cause)),
    };
  }

  function heard(
    key: string,
    message: Extract<ServerMessage, { type: 'partial' | 'final' }>,
    final: boolean,
  ) {
    // Provider words during playback may be echo, including on a Gate B
    // platform. Only the local acoustic detector may take over a spoken reply.
    if (deps.player && deps.player.state() !== 'idle') return;
    const segment: CaptionSegment = {
      key,
      text: message.text,
      stable:
        'stable' in message
          ? Math.max(0, Math.min(message.stable, message.text.length))
          : message.text.length,
      final,
      startMs: message.startMs,
      endMs: message.endMs,
    };
    set({ segments: upsertSegment(get().segments, segment) });
    if (message.text.length === 0) return;
    resetIdle();
    // Speech inside the undo window stops the countdown; its turn merges.
    if (pending) holdPending('paused');
    if (HEARS_FROM.has(get().phase)) setPhase('hearing');
  }

  // ── Turns ────────────────────────────────────────────────────────────────

  function openTurn() {
    if (!link || link.openTurn !== null) return;
    const composer = binding?.composer;
    turnStart = {
      startedAt: deps.now(),
      draftText: composer?.getState().text ?? '',
      attachments: composer?.attachments() ?? 0,
    };
    lastVoicedAt = null;
    link.beginTurn();
  }

  function toggleTalk() {
    if (!link || !barActive(get().phase)) return;
    if (replying(get().phase)) interruptReply(barge?.interrupt() ?? null);
    if (pending) holdPending('paused');
    if (link.openTurn !== null) return commitTurn('tap');
    openTurn();
    setPhase('hearing');
    resetIdle();
  }

  function cancelTalk() {
    if (!link || link.openTurn === null) return;
    commitTurn('endpoint', undefined, true);
  }

  function commitNow() {
    // ✓ while a reply plays is a tap over it: the reply stops, the turn stays open.
    if (replying(get().phase) && deps.player && deps.player.state() !== 'idle')
      return stopSpeaking();
    if (link?.openTurn !== null) commitTurn('tap');
  }

  /** A tap or key over the reply: it stops within 150 ms and voice mode listens again. */
  function stopSpeaking() {
    interruptReply(barge?.interrupt() ?? null);
    if (replying(get().phase)) setPhase('ready');
    resetIdle();
  }

  function commitTurn(commit: VoiceCommit, words?: number, discard = false) {
    const current = link;
    if (!current || current.openTurn === null) return;
    const start = turnStart ?? { startedAt: deps.now(), draftText: '', attachments: 0 };
    turnStart = null;
    const gen = generation;
    setPhase(pending ? 'pending' : 'committing');
    current.commit().then(
      (message) => {
        if (gen !== generation) return;
        const context = {
          commit,
          words,
          start,
          sessionId: message.sessionId,
          provider: message.provider,
          link: current,
        };
        if (discard) dropTurn();
        else onCommitted(message, context);
        if (get().style === 'handsfree' && !degradedToUtterance && barActive(get().phase))
          openTurn();
      },
      () => {},
    );
    resetIdle();
  }

  /** The turn's finals move out of the live text; a new turn's partials stay. */
  function takeTurnSegments(): CaptionSegment[] {
    const segments = get().segments;
    set({ segments: segments.filter((segment) => !segment.final) });
    return segments.filter((segment) => segment.final);
  }

  function dropTurn() {
    takeTurnSegments();
    if (pending) return resumePending();
    setPhase('ready');
  }

  type CommitContext = {
    commit: VoiceCommit;
    words: number | undefined;
    start: TurnStart;
    sessionId: string;
    provider: string;
    link: VoiceLinkApi;
  };

  function onCommitted(message: CommittedMessage, context: CommitContext) {
    const segments = takeTurnSegments();
    let utterance = createUtterance({
      sessionId: context.sessionId,
      turn: message.turn ?? -1,
      commit: context.commit,
      lang: message.lang,
      words: context.words,
      startedAt: context.start.startedAt,
      text: message.text,
    });
    if (isEmptyUtterance(utterance)) {
      if (pending) return resumePending();
      return setPhase(
        'ready',
        context.commit === 'endpoint' ? {} : { issue: voiceIssue('no_speech') },
      );
    }
    const merged = pending;
    if (merged) {
      deps.timers.clearTimeout(merged.timer);
      utterance = mergeUtterances(merged.utterance, utterance);
    }
    const shown = [...(merged ? (get().pending?.segments ?? []) : []), ...segments];
    pending = null;
    const composer = binding?.composer;
    const decision = commitOutcome({
      voiceOn: barActive(get().phase),
      policyAllows: Boolean(voiceConfig()?.policy.voiceMode),
      commit: context.commit,
      words: utterance.words,
      handsFreeAvailable: Boolean(voiceConfig()?.handsFree.available) && !degradedToUtterance,
      endpointVerified: message.endpointVerified === true,
      draftEmptyAtStart: context.start.draftText.trim() === '' && context.start.attachments === 0,
      draftChanged:
        (composer?.getState().text ?? '') !== context.start.draftText ||
        (composer?.attachments() ?? 0) !== context.start.attachments,
      empty: false,
      degraded: message.degraded || get().degraded,
      focus: deps.focus.current(),
      latestAssistantMessageId: binding?.latestAssistantMessageId() ?? null,
      visible: deps.visible(),
      online: deps.online(),
    });
    const next: Pending = {
      utterance,
      binding: binding!,
      link: context.link,
      sessionId: context.sessionId,
      provider: message.provider || context.provider,
      degraded: message.degraded || get().degraded,
      timer: null,
      undoMs: 0,
      committedAt: deps.now(),
    };
    dispatch(decision.outcome, decision.guard, next, shown);
  }

  function dispatch(
    outcome: CommitOutcome,
    guard: GuardId | null,
    next: Pending,
    shown: CaptionSegment[],
  ) {
    if (!binding) return;
    if (outcome === 'insert') {
      insertUtterance(binding.composer, next.utterance);
      return enterEditing();
    }
    if (outcome === 'draft') {
      pending = next;
      return pause(null);
    }
    if (outcome === 'pending')
      return showPending(next, shown, PENDING_REASONS[guard ?? 'G4'] ?? 'always_ask');
    const undo = undoFor(next.utterance.commit);
    if (undo === 'always_ask') return showPending(next, shown, 'always_ask');
    next.undoMs = undo;
    showPending(next, shown, 'countdown');
  }

  function showPending(next: Pending, shown: CaptionSegment[], reason: PendingReason) {
    pending = next;
    const counting = reason === 'countdown';
    const deadline = counting ? deps.now() + next.undoMs : null;
    if (counting) next.timer = deps.timers.setTimeout(() => trySend(false), next.undoMs);
    pendingId += 1;
    setPhase('pending', {
      pending: { id: pendingId, segments: shown, deadline, undoMs: next.undoMs, reason },
      announced: get().announced + 1,
    });
    resetIdle();
  }

  /** Speech resumed or a press: the countdown stops until the merged turn commits. */
  function holdPending(reason: PendingReason) {
    const view = get().pending;
    if (!pending || !view || view.deadline === null) return;
    deps.timers.clearTimeout(pending.timer);
    pending.timer = null;
    set({ pending: { ...view, deadline: null, reason } });
  }

  function resumePending() {
    const view = get().pending;
    if (!pending || !view) return setPhase('ready');
    if (view.reason !== 'paused') return setPhase('pending');
    pending.timer = deps.timers.setTimeout(() => trySend(false), pending.undoMs);
    setPhase('pending', {
      pending: { ...view, deadline: deps.now() + pending.undoMs, reason: 'countdown' },
    });
  }

  // ── Sending ──────────────────────────────────────────────────────────────

  function trySend(explicit: boolean) {
    const kept = pending;
    if (!kept || cancelling) return;
    deps.timers.clearTimeout(kept.timer);
    kept.timer = null;
    const decision = sendOutcome({
      visible: deps.visible(),
      online: deps.online(),
      gateAllows: kept.binding === binding && kept.binding.gateAllows(),
      running: kept.binding.running(),
      sameThread: kept.binding === binding,
      sameSession: kept.link === link,
      explicit,
    });
    if (decision.outcome === 'draft') return pause(null);
    if (decision.outcome === 'cancel_then_send') {
      void cancelThenSend(kept, explicit);
      return;
    }
    pending = null;
    send(kept, explicit);
  }

  async function cancelThenSend(kept: Pending, explicit: boolean) {
    const gen = generation;
    cancelling = kept;
    setPhase('committing');
    try {
      await kept.binding.cancelRun();
    } catch {
      if (gen === generation) pause(null);
      return;
    }
    // Teardown keeps the pending turn with its original thread and clears it
    // on sign-out. An old cancellation must not restore or send it afterwards.
    if (gen !== generation) return;
    cancelling = null;
    // Recheck visibility, connection and authority after the asynchronous stop.
    trySend(explicit);
  }

  function send(kept: Pending, explicit: boolean) {
    // A new voice turn supersedes every previously issued action question.
    clearIssuedQuestions();
    const readAloud = readAloudOn();
    const body = voiceTurnBody(kept.utterance, {
      style: get().style,
      sessionId: kept.sessionId,
      provider: kept.provider,
      degraded: kept.degraded,
      autoSent: !explicit,
      undoMs: kept.undoMs,
      readAloud,
      interrupted: lastInterrupted,
      firstAudioMs: lastFirstAudioMs,
      playback: Boolean(deps.config().readAloud?.available),
    });
    lastInterrupted = null;
    lastFirstAudioMs = null;
    previousReplyMessageId = lastReplyMessageId;
    lastSent = { binding: kept.binding, link: kept.link, messageId: null };
    live = {
      binding: kept.binding,
      link: kept.link,
      sessionId: kept.sessionId,
      sentAt: deps.now(),
      before: kept.binding.latestAssistantMessageId(),
      messageId: null,
      ran: false,
      started: false,
      done: false,
      spokenParts: new Map(),
      spokenFed: '',
      nextSpokenSequence: 0,
      toolTiming: createLaneToolTiming(),
      firstAudioAt: null,
      spokenTextAt: null,
      readAloud,
      threadId: kept.binding.threadId,
    };
    if (laneTimingEnabled(voiceConfig()?.laneTiming))
      publishLaneTiming({ undoMs: Math.max(0, deps.now() - kept.committedAt) });
    armVoiceTurn(body, deps.now());
    setPhase('sending', { pending: null, issue: null });
    try {
      kept.binding.append(voiceTurnMessage(kept.utterance, body));
    } catch {
      // The runtime refused the turn: it is kept as a draft, never retried.
      live = null;
      pending = kept;
      pause(null);
    }
    resetIdle();
  }

  // ── The reply ────────────────────────────────────────────────────────────

  function onThread(snapshot: ThreadSnapshot) {
    const turn = live;
    if (!turn || turn.binding !== binding) return;
    if (snapshot.running) turn.ran = true;
    const last = snapshot.last;
    // The runtime shows an empty assistant placeholder from the append on; a
    // reply has content, which arrives only after the request went out.
    const replied = Boolean(
      last && last.role === 'assistant' && last.id !== turn.before && last.parts > 0,
    );
    // The thread runs from the append, before the transport builds the
    // request: the body stays armed until the reply shows the request went
    // out, or the run ends.
    if (replied && !turn.started) {
      turn.started = true;
      disarmVoiceTurn();
    }
    if (last && replied) {
      turn.messageId = last.id;
      lastReplyMessageId = last.id;
      if (lastSent?.link === turn.link) lastSent.messageId = last.id;
      if (last.text.length > 0 && get().phase === 'sending') setPhase('answering');
      spoken.begin(last.id);
    }
    if (!snapshot.running && (turn.ran || replied)) {
      turn.done = true;
      disarmVoiceTurn();
      emitSpoken(turn, true);
      settleReply();
    }
  }

  function onToolTiming(value: unknown, resumed = false) {
    if (resumed || !laneTimingEnabled(voiceConfig()?.laneTiming) || !live) return;
    if (live.binding !== binding || live.threadId !== binding?.threadId) return;
    const timing = parseToolTiming(value);
    if (!timing || timing.messageId === live.before || timing.messageId === previousReplyMessageId)
      return;
    if (!live.messageId) live.messageId = timing.messageId;
    if (live.messageId !== timing.messageId) return;
    publishLaneTiming({ toolMs: live.toolTiming(timing) });
  }

  function onSpokenOutput(value: unknown, resumed = false) {
    if (!live || !live.readAloud) return;
    if (live.binding !== binding || live.threadId !== binding?.threadId) return;
    const candidate = value as { messageId?: unknown };
    if (typeof candidate?.messageId !== 'string') return;
    if (candidate.messageId === live.before || candidate.messageId === previousReplyMessageId)
      return;
    if (!live.messageId) live.messageId = candidate.messageId;
    if (live.messageId !== candidate.messageId) return;
    if (laneTimingEnabled(voiceConfig()?.laneTiming) && live.spokenParts.size === 0) {
      live.spokenTextAt = deps.now();
      markLaneSpokenText(live.spokenTextAt);
      publishLaneTiming({ modelToTextMs: live.spokenTextAt - live.sentAt });
    }
    spoken.begin(live.messageId);
    spoken.accept(value, resumed);
  }

  function emitSpoken(turn: Live, done: boolean) {
    const ready: SpokenPart[] = [];
    let sequence = turn.nextSpokenSequence;
    while (turn.spokenParts.has(sequence)) {
      ready.push(turn.spokenParts.get(sequence)!);
      sequence += 1;
    }
    const text = [...(turn.spokenFed ? [turn.spokenFed] : []), ...ready.map((part) => part.text)]
      .filter(Boolean)
      .join('\n\n');
    if (!text || (!done && text === turn.spokenFed)) return;
    const source = messageSource(turn);
    if (!source) return;
    turn.nextSpokenSequence = sequence;
    if (text !== turn.spokenFed) turn.spokenFed = text;
    deps.player?.feed(source, text, done, { rate: prefs()?.rate ?? 1 });
  }

  function messageSource(turn: Live): SpeakSource | null {
    const answer = deps.liveAnswer();
    if (answer && !turn.done) turn.threadId ??= answer.threadId;
    if (!turn.threadId || !turn.messageId) return null;
    return {
      kind: 'message',
      threadId: turn.threadId,
      messageId: turn.messageId,
      channel: 'spoken',
      ...(answer && !turn.done ? { streamId: answer.streamId } : {}),
    };
  }

  function onPlayer() {
    const state = deps.player?.state() ?? 'idle';
    const turn = live;
    if (state === 'speaking' && turn && turn.firstAudioAt === null) {
      turn.firstAudioAt = deps.now();
      lastFirstAudioMs = turn.firstAudioAt - turn.sentAt;
    }
    const phase = get().phase;
    if (state === 'speaking' && SPEAKS_FROM.has(phase)) setPhase('speaking');
    if (state === 'idle') settleReply();
  }

  /** The reply is complete and silent: back to ready. */
  function settleReply() {
    const turn = live;
    if (turn && !turn.done) return;
    if (deps.player && deps.player.state() !== 'idle') return;
    live = null;
    if (replying(get().phase)) setPhase('ready');
    resetIdle();
  }

  // ── Pending actions ──────────────────────────────────────────────────────

  function sendNow() {
    if (pending) trySend(true);
  }

  function editPending() {
    const kept = pending;
    if (!kept || !binding) return;
    deps.timers.clearTimeout(kept.timer);
    pending = null;
    insertUtterance(binding.composer, kept.utterance);
    enterEditing();
  }

  function discardPending() {
    const kept = pending;
    if (!kept) return;
    deps.timers.clearTimeout(kept.timer);
    pending = null;
    setPhase('ready', { pending: null });
    resetIdle();
  }

  function escape() {
    if (pending) return discardPending();
    if (link?.openTurn !== null && get().phase === 'hearing') return cancelTalk();
    exit();
  }

  // ── voice_control (a model tool call, never a transcript) ────────────────

  function applyControl(toolCallId: string, result: VoiceControlResult, messageId: string | null) {
    const sent = lastSent;
    if (applied.has(toolCallId) || !barActive(get().phase) || !sent || !messageId) return false;
    // Only the reply to the turn this client sent, in the voice session still open.
    if (sent.link !== link || sent.binding !== binding || sent.messageId !== messageId)
      return false;
    applied.add(toolCallId);
    CONTROL_EFFECTS[result.action](result);
    return true;
  }

  const CONTROL_EFFECTS: Record<
    VoiceControlResult['action'],
    (result: VoiceControlResult) => void
  > = {
    stop: () => {
      interruptReply(
        deps.player && deps.player.state() !== 'idle' ? deps.player.stop('user') : null,
      );
    },
    repeat: (result) => {
      const messageId = result.messageId ?? previousReplyMessageId;
      if (messageId && deps.player) deps.player.replay(messageId);
    },
    exit: () => exit(),
    rate: (result) => {
      if (typeof result.rate === 'number')
        void deps.savePrefs({ rate: result.rate }).catch(() => {});
    },
  };

  // ── Binding, visibility ──────────────────────────────────────────────────

  function unbind(previous: VoiceBinding) {
    if (binding !== previous) return;
    clearInterruptions();
    clearIssuedQuestions();
    // Keep a pending turn with its original thread, never in a composer that
    // the next binding may reuse. bind clears it if the user changed.
    const kept = pending;
    pending = null;
    if (kept) deps.timers.clearTimeout(kept.timer);
    if (kept?.binding.threadId) keepDraft(kept.binding.threadId, kept.utterance);
    exit();
    binding = null;
  }

  function bind(next: VoiceBinding) {
    if (binding !== next) clearIssuedQuestions();
    if (binding && binding !== next) unbind(binding);
    if (lastBoundUserId !== next.userId || next.userId === null) clearDrafts();
    lastBoundUserId = next.userId;
    binding = next;
    if (next.userId && next.threadId) restoreDrafts(next.threadId, next.composer);
    return () => unbind(next);
  }

  function onVisibility(visible: boolean) {
    if (!visible && get().phase !== 'off' && get().phase !== 'paused' && get().phase !== 'editing')
      pause(voiceIssue('hidden'));
  }

  deps.player?.subscribe(onPlayer);

  return {
    enter,
    exit,
    toggleTalk,
    cancelTalk,
    commitNow,
    sendNow,
    editPending,
    discardPending,
    escape,
    stopSpeaking,
    dismiss: () => {
      if (get().phase === 'paused') setPhase('off', leftState(null));
      else set({ issue: null });
    },
    bind,
    onThread,
    onVisibility,
    applyControl,
    onSpokenOutput,
    onToolTiming,
  };
}

/** voice-structural: which guard left the turn waiting for a tap, as the bubble's reason. */
const PENDING_REASONS: Partial<Record<GuardId, PendingReason>> = {
  G2: 'endpoint',
  G4: 'degraded',
  G5: 'focus',
};

/** voice-structural: phases the provider's first words move to hearing (hands-free, full duplex). */
const HEARS_FROM: ReadonlySet<VoiceState['phase']> = new Set<VoiceState['phase']>([
  'ready',
  'pending',
  'sending',
  'answering',
  'speaking',
  'ducked',
]);

/** voice-structural: phases in which the reply is read aloud (full duplex ducks it). */
const READING: ReadonlySet<VoiceState['phase']> = new Set<VoiceState['phase']>([
  'speaking',
  'ducked',
]);

/** voice-structural: phases a reply's first audio moves to speaking. */
const SPEAKS_FROM: ReadonlySet<VoiceState['phase']> = new Set<VoiceState['phase']>([
  'ready',
  'sending',
  'answering',
]);
